import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTasks } from '../../contexts/TaskContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { PanelLayoutContext } from '../../layouts/PanelLayoutContext'; // Assuming this is where sidebar state is
import {
    Terminal,
    X,
    Maximize2,
    Minimize2,
    Loader2,
    CheckCircle2,
    XCircle,
    Trash2,
    ChevronUp,
    ChevronDown,
    Activity,
    LineChart,
    Cpu,
    ExternalLink,
    TerminalSquare
} from 'lucide-react';
import { Box, Typography, IconButton, Tooltip, Badge } from '@mui/material';

const SystemHelperPanel = () => {
    // 1. Hooks (Always at the top)
    const {
        tasks,
        activeTaskId,
        setActiveTaskId,
        isPanelOpen,
        setIsPanelOpen,
        activeTab,
        setActiveTab,
        clearTasks
    } = useTasks();
    const { isRoot } = usePermissions();
    const layoutContext = useContext(PanelLayoutContext);

    const [panelHeight, setPanelHeight] = useState('450px');
    const [isMaximized, setIsMaximized] = useState(false);
    const [isMinimized, setIsMinimized] = useState(true);
    const scrollRef = useRef(null);

    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [tasks, activeTaskId, activeTab]);

    // 2. Logic
    const isSidebarVisible = layoutContext?.sidebarState === 1; // SIDEBAR_STATE.VISIBLE
    const activeTask = tasks.find(t => t.id === activeTaskId);
    const runningTasksCount = tasks.filter(t => t.status === 'running').length;

    // 3. Render Helpers
    const parseLogs = (logs) => {
        if (!logs) return [];

        const ansiRegex = /\u001b\[([0-9;]*)m/g;

        return logs.split('\n').map((line, i) => {
            // Check if the line has ANSI codes
            if (!line.includes('\x1b[')) {
                let color = 'text-gray-300';
                if (line.includes('[SUCCESS]') || line.includes('✅')) color = 'text-green-400';
                if (line.includes('[ERROR]') || line.includes('❌')) color = 'text-red-400';
                if (line.includes('[WARNING]') || line.includes('⚠️')) color = 'text-yellow-400';
                if (line.includes('[INFO]') || line.includes('ℹ️')) color = 'text-blue-400';

                return (
                    <div key={i} className={`font-mono text-sm py-0.5 whitespace-pre-wrap ${color}`}>
                        {line}
                    </div>
                );
            }

            // Robust ANSI Parsing
            const segments = [];
            let lastIndex = 0;
            let currentStyles = { color: '#d1d5db' }; // text-gray-300
            let match;

            const colorMap = {
                '30': '#000000', '31': '#ef4444', '32': '#22c55e', '33': '#eab308',
                '34': '#3b82f6', '35': '#a855f7', '36': '#06b6d4', '37': '#ffffff',
                '90': '#6b7280', '91': '#f87171', '92': '#4ade80', '93': '#facc15',
                '94': '#60a5fa', '95': '#c084fc', '96': '#22d3ee', '97': '#ffffff'
            };

            while ((match = ansiRegex.exec(line)) !== null) {
                // Background text before the match
                if (match.index > lastIndex) {
                    segments.push({
                        text: line.substring(lastIndex, match.index),
                        style: { ...currentStyles }
                    });
                }

                // Update styles based on ANSI code
                const codes = match[1].split(';');
                codes.forEach(code => {
                    if (code === '0' || code === '') {
                        currentStyles = { color: '#d1d5db' };
                    } else if (colorMap[code]) {
                        currentStyles.color = colorMap[code];
                    } else if (code === '1') {
                        currentStyles.fontWeight = 'bold';
                    }
                });

                lastIndex = ansiRegex.lastIndex;
            }

            // Final segment
            if (lastIndex < line.length) {
                segments.push({
                    text: line.substring(lastIndex),
                    style: { ...currentStyles }
                });
            }

            return (
                <div key={i} className="font-mono text-sm py-0.5 whitespace-pre-wrap flex flex-wrap">
                    {segments.map((seg, si) => (
                        <span key={si} style={seg.style}>{seg.text}</span>
                    ))}
                </div>
            );
        });
    };

    // 4. Conditional Return (After all hooks)
    if (!isRoot) return null;

    const toggleMinimize = () => {
        setIsMinimized(!isMinimized);
        if (isMaximized) setIsMaximized(false);
    };

    const toggleMaximize = () => {
        setIsMaximized(!isMaximized);
        setIsMinimized(false);
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'logs':
                return (
                    <div className="h-full flex flex-col bg-black/40">
                        {/* Selected Task List Header */}
                        <div className="flex items-center gap-2 px-4 py-2 border-b border-white/5 bg-white/5 overflow-x-auto no-scrollbar">
                            {tasks.length === 0 ? (
                                <div className="text-gray-500 text-sm italic py-1">Henüz bir görev başlatılmadı</div>
                            ) : (
                                tasks.map(task => (
                                    <button
                                        key={task.id}
                                        onClick={() => setActiveTaskId(task.id)}
                                        className={`flex items-center gap-2 px-3 py-1 rounded-md transition-all whitespace-nowrap ${activeTaskId === task.id
                                            ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30 shadow-[0_0_10px_rgba(59,130,246,0.1)]'
                                            : 'hover:bg-white/5 text-gray-400 border border-transparent'
                                            }`}
                                    >
                                        {task.status === 'running' && <Loader2 className="w-3 h-3 animate-spin" />}
                                        {task.status === 'success' && <CheckCircle2 className="w-3 h-3 text-green-500" />}
                                        {task.status === 'error' && <XCircle className="w-3 h-3 text-red-500" />}
                                        <span className="text-xs font-bold">{task.title}</span>
                                    </button>
                                ))
                            )}
                        </div>
                        {/* Log Viewer Area */}
                        <div
                            ref={scrollRef}
                            className="flex-1 overflow-y-auto p-4 custom-scrollbar bg-black/20"
                        >
                            {activeTask ? (
                                <div className="max-w-full">
                                    <div className="flex items-center gap-2 mb-4 text-[10px] font-mono text-gray-500 border-b border-white/5 pb-2">
                                        <span className="text-blue-500/60 uppercase">Task Engine</span>
                                        <span>•</span>
                                        <span>ID: {activeTask.id}</span>
                                        <span>•</span>
                                        <span>{new Date(activeTask.startTime).toLocaleTimeString()}</span>
                                    </div>
                                    {parseLogs(activeTask.logs)}
                                    {activeTask.status === 'running' && (
                                        <div className="flex items-center gap-2 mt-4 text-blue-400 border-t border-white/5 pt-2">
                                            <Loader2 className="w-3 h-3 animate-spin" />
                                            <span className="text-xs font-mono animate-pulse">CANLI AKIŞ DEVAM EDİYOR...</span>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="h-full flex items-center justify-center text-gray-500 opacity-40">
                                    <div className="text-center">
                                        <TerminalSquare className="w-16 h-16 mx-auto mb-3" />
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>AKTİF LOG SEÇİLMEDİ</Typography>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 'tasks':
                return (
                    <div className="h-full flex flex-col overflow-hidden bg-[#0a0c10]">
                        {/* Task Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b border-white/5 bg-[#0d0f14]">
                            <div>
                                <Typography variant="h6" sx={{ color: 'white', fontWeight: 800, fontSize: '0.9rem', letterSpacing: '0.05em' }}>SİSTEM GÖREV YÖNETİCİSİ</Typography>
                                <Typography variant="caption" sx={{ color: 'gray', fontWeight: 600 }}>Oturumdaki tüm arka plan işlemleri listelenmektedir</Typography>
                            </div>
                            <button
                                onClick={clearTasks}
                                className="flex items-center gap-2 px-3 py-1.5 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded-lg transition-all text-[10px] font-black border border-red-500/20"
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                                TÜM GEÇMİŞİ SİL
                            </button>
                        </div>

                        {/* Task List Table */}
                        <div className="flex-1 overflow-y-auto custom-scrollbar p-4">
                            {tasks.length === 0 ? (
                                <div className="h-full flex flex-col items-center justify-center text-gray-600 space-y-4 opacity-30">
                                    <Activity className="w-16 h-16" />
                                    <p className="text-sm font-bold uppercase tracking-widest">Henüz bir görev kaydı bulunmuyor</p>
                                </div>
                            ) : (
                                <div className="space-y-1">
                                    {/* Table Header Labels */}
                                    <div className="flex items-center px-4 py-2 text-[10px] font-black text-gray-600 uppercase tracking-widest border-b border-white/5 mb-2">
                                        <div className="w-10">DURUM</div>
                                        <div className="flex-1 px-4">İŞLEM ADI / ID</div>
                                        <div className="w-40 text-right">BAŞLANGIÇ ZAMANI</div>
                                        <div className="w-24 text-right">AKSİYON</div>
                                    </div>

                                    {tasks.map(task => (
                                        <div
                                            key={task.id}
                                            onClick={() => { setActiveTaskId(task.id); setActiveTab('logs'); }}
                                            className={`group flex items-center px-4 py-1.5 rounded-lg transition-all cursor-pointer border h-8 ${activeTaskId === task.id
                                                ? 'bg-blue-500/10 border-blue-500/30'
                                                : 'bg-white/[0.01] border-white/5 hover:border-white/10 hover:bg-white/[0.03]'
                                                }`}
                                        >
                                            {/* Status Icon */}
                                            <div className="w-8 flex justify-center">
                                                {task.status === 'running' ? (
                                                    <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />
                                                ) : task.status === 'success' ? (
                                                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                                                ) : (
                                                    <XCircle className="w-4 h-4 text-red-500" />
                                                )}
                                            </div>

                                            {/* Task Info */}
                                            <div className="flex-1 px-4 min-w-0">
                                                <div className="flex items-center gap-3">
                                                    <span className={`text-[11px] font-black uppercase tracking-tight truncate ${activeTaskId === task.id ? 'text-blue-400' : 'text-gray-300 group-hover:text-white'
                                                        }`}>
                                                        {task.title}
                                                    </span>
                                                    <span className="text-[9px] font-mono text-gray-600 bg-black/40 px-1.5 py-0.5 rounded border border-white/5 shrink-0">
                                                        #{task.id.slice(0, 6)}
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Time */}
                                            <div className="w-32 text-right shrink-0">
                                                <div className="text-[10px] font-mono text-gray-500">
                                                    {new Date(task.startTime).toLocaleTimeString([], { hour12: false })}
                                                </div>
                                            </div>

                                            {/* Action */}
                                            <div className="w-12 flex justify-end shrink-0">
                                                <button className={`p-1.5 rounded transition-all ${activeTaskId === task.id ? 'bg-blue-500/20 text-blue-400' : 'text-gray-600 group-hover:text-blue-400'
                                                    }`}>
                                                    <ExternalLink className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                );
            case 'ssh':
                return (
                    <div className="h-full flex flex-col items-center justify-center p-12 text-center bg-[#0a0c10]">
                        <div className="relative mb-8">
                            <Terminal className="w-24 h-24 text-blue-500/20" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <Cpu className="w-10 h-10 text-blue-400 animate-pulse" />
                            </div>
                        </div>
                        <Typography variant="h5" sx={{ color: 'white', mb: 2, fontWeight: 800 }}>TERM-LYSEP V2</Typography>
                        <div className="max-w-md bg-black/40 border border-white/5 p-6 rounded-2xl">
                            <Typography variant="body2" sx={{ color: 'gray', mb: 4, lineHeight: 1.6 }}>
                                Uzak sunucu yönetimi için geliştirilen gerçek zamanlı SSH tünelleme ve terminal arayüzü yakında aktif edilecektir. Bu modül üzerinden tüm sunucularınıza doğrudan müdahale edebileceksiniz.
                            </Typography>
                            <div className="flex gap-2 justify-center">
                                <span className="px-3 py-1 bg-white/5 rounded text-[10px] text-blue-400 font-mono border border-blue-500/20">WSS_PROTOCOL_READY</span>
                                <span className="px-3 py-1 bg-white/5 rounded text-[10px] text-blue-400 font-mono border border-blue-500/20">SSH2_AUTH_READY</span>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    const sidebarWidth = isSidebarVisible ? '280px' : '0px';

    if (isMinimized) {
        return (
            <div
                className="fixed bottom-0 z-[1000] h-10 bg-[#0f1115] border-t border-white/10 flex items-center justify-between px-6 transition-all duration-300 cursor-pointer hover:bg-[#16181d] shadow-[0_-10px_30px_rgba(0,0,0,0.5)]"
                style={{ left: sidebarWidth, right: 0 }}
                onClick={toggleMinimize}
            >
                <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-white">
                        <Activity className="w-4 h-4 text-[#fd7000]" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-gray-200">System Helper Panel</span>
                    </div>
                    {runningTasksCount > 0 && (
                        <div className="flex items-center gap-3 px-3 py-1 bg-[#fd7000]/10 border border-[#fd7000]/20 rounded-full">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-ping" />
                            <span className="text-[10px] text-[#fd7000] font-black">{runningTasksCount} AKTİF İŞLEM</span>
                        </div>
                    )}
                </div>
                <div className="flex items-center gap-1">
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleMaximize(); }}>
                        <Maximize2 className="w-3 h-3 text-gray-500" />
                    </IconButton>
                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); toggleMinimize(); }}>
                        <ChevronUp className="w-4 h-4 text-[#fd7000]" />
                    </IconButton>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`fixed bottom-0 z-[1000] bg-[#0c0e12] border-t border-white/10 shadow-[0_-20px_50px_rgba(0,0,0,0.8)] transition-all duration-300 flex flex-col`}
            style={{
                left: sidebarWidth,
                right: 0,
                height: isMaximized ? '90vh' : panelHeight,
                transform: isPanelOpen ? 'translateY(0)' : 'translateY(100%)'
            }}
        >
            {/* Header / Tabs Container */}
            <div className="flex items-center justify-between px-4 py-3 bg-[#16181d] border-b border-white/10 select-none">
                <div className="flex items-center gap-8">
                    <div className="flex items-center gap-3 pr-6 border-r border-white/5">
                        <Activity className="w-5 h-5 text-[#fd7000] animate-pulse" />
                        <div>
                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 900, fontSize: '0.7rem', leadingHeight: 1, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                                LYSEP MASTER
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'gray', fontSize: '0.6rem', fontWeight: 600, display: 'block', mt: -0.5 }}>
                                ROOT_SHELL_CORE
                            </Typography>
                        </div>
                    </div>

                    {/* Main Category Tabs */}
                    <div className="flex items-center gap-2">
                        {[
                            { id: 'logs', label: 'TERMINAL_LOG', icon: TerminalSquare },
                            { id: 'tasks', label: 'TASK_MANAGER', icon: LineChart },
                            { id: 'ssh', label: 'REMOTE_SSH', icon: Cpu }
                        ].map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-3 px-5 py-2 rounded-xl transition-all duration-300 relative group ${activeTab === tab.id
                                    ? 'bg-[#fd7000] text-white shadow-[0_0_20px_rgba(253,112,0,0.2)]'
                                    : 'text-gray-500 hover:text-gray-300 hover:bg-white/5'
                                    }`}
                            >
                                <tab.icon className={`w-4 h-4 transition-transform duration-300 ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-110'}`} />
                                <span className="text-[11px] font-black uppercase tracking-tighter">{tab.label}</span>
                                {tab.id === 'tasks' && tasks.length > 0 && (
                                    <span className={`text-[10px] px-2 py-0.5 rounded-md font-bold ${activeTab === tab.id ? 'bg-black/20 text-white' : 'bg-[#fd7000]/20 text-[#fd7000]'
                                        }`}>
                                        {tasks.length}
                                    </span>
                                )}
                                {activeTab === tab.id && (
                                    <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-1 h-1 bg-[#fd7000] rounded-full" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Tooltip title={isMaximized ? "Pencere Moduna Geç" : "Maksimum Boyut"}>
                        <IconButton size="small" onClick={toggleMaximize} sx={{ color: 'gray', bgcolor: 'rgba(255,255,255,0.03)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                            {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Gizle / Küçült">
                        <IconButton size="small" onClick={toggleMinimize} sx={{ color: 'gray', bgcolor: 'rgba(255,255,255,0.03)', '&:hover': { color: 'white', bgcolor: 'rgba(255,255,255,0.1)' } }}>
                            <ChevronDown className="w-5 h-5" />
                        </IconButton>
                    </Tooltip>
                </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 relative overflow-hidden bg-black/40 backdrop-blur-3xl">
                {renderTabContent()}

                {/* Visual Enhancements */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
                <div className="absolute inset-0 pointer-events-none shadow-[inset_0_0_100px_rgba(0,0,0,0.2)]" />
            </div>

            {/* Real-time Status Footer */}
            <div className="flex items-center justify-between px-6 py-2 bg-[#050608] border-t border-white/5 text-[9px] text-gray-500 font-mono tracking-widest">
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${runningTasksCount > 0 ? 'bg-blue-500 shadow-[0_0_10px_rgba(59,130,246,0.5)] animate-pulse' : 'bg-gray-700'}`} />
                        <span className="font-bold">STATUS: {runningTasksCount > 0 ? 'PROCESSING' : 'IDLE'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-bold">THREADS:</span>
                        <span className="text-gray-400">08_CORE_VIRTUAL</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-600">
                        <span className="font-bold">LOAD:</span>
                        <span className="text-gray-400">{(Math.random() * 2 + 0.5).toFixed(2)}%</span>
                    </div>
                </div>
                <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                        <span className="font-bold">BUILD:</span>
                        <span className="text-gray-400">LYSEP_STABLE_V2.1</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-bold">LATENCY:</span>
                        <span className="text-green-500/80">14ms</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="font-bold text-[#fd7000]">MASTER_NODE_1</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SystemHelperPanel;
